'use client';

import Image from 'next/image';
import Script from 'next/script';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Navigation from '../components/Navigation';
import RandomBackground from '../components/RandomBackground';

type ContactInfo = {
  email: string;
  phoneDisplay: string;
  phoneLink: string;
};

type TurnstileApi = {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark';
    }
  ) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  // Cloudflare Turnstile injects this on the global scope.
  var turnstile: TurnstileApi | undefined;
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';
const REVEAL_BUTTON_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/35 px-3 py-1.5 text-xs font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 md:text-sm';

function RevealButton({
  onClick,
  isLoading,
  className = '',
}: Readonly<{ onClick: () => Promise<void>; isLoading: boolean; className?: string }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${REVEAL_BUTTON_CLASS} ${className}`}
      disabled={isLoading}
    >
      <Image src="/lock.svg" alt="" width={14} height={14} aria-hidden className="icon-white opacity-80" />
      {isLoading ? 'Revealing...' : 'Click to reveal'}
      <Image src="/lock_open.svg" alt="" width={14} height={14} aria-hidden className="icon-white opacity-80" />
    </button>
  );
}

function GlassCard({
  children,
  className = '',
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return <div className={`glass-card rounded-2xl ${className}`}>{children}</div>;
}

function ContactInfoPanel({
  contactInfo,
  onReveal,
  isLoading,
}: Readonly<{
  contactInfo: ContactInfo | null;
  onReveal: () => Promise<void>;
  isLoading: boolean;
}>) {
  return (
    <GlassCard className="overflow-hidden">
      <div className="grid gap-0 md:grid-cols-2">
        <div className="p-8 md:p-10">
          <h2 className="text-lg font-semibold text-white md:text-xl">Location</h2>
          <div className="mt-6 space-y-1 text-sm leading-relaxed text-white/75 md:text-base">
            <p>Creative Home Inventions, LLC</p>
            <p>4450 S. Ridge Rd</p>
            <p>McKinney, TX 75070</p>
            <p className="pt-2 text-sm font-medium text-white/85 md:text-base">
              Tel:{' '}
              {contactInfo ? (
                <a
                  href={`tel:${contactInfo.phoneLink}`}
                  className="font-semibold text-white/85 transition-colors hover:text-[#67b4ff]"
                >
                  {contactInfo.phoneDisplay}
                </a>
              ) : (
                <RevealButton onClick={onReveal} isLoading={isLoading} />
              )}
            </p>
          </div>
        </div>
        <div className="border-t border-white/10 p-8 md:border-l md:border-t-0 md:p-10">
          <h2 className="text-lg font-semibold text-white md:text-xl">Hours of Operation</h2>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-white/75 md:text-base">
            <p>M - F: 9:00am - 5:00pm</p>
            <p>Sat: 9:00am - 1:00pm</p>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

function QuickContactStrip({
  contactInfo,
  onReveal,
  isLoading,
}: Readonly<{
  contactInfo: ContactInfo | null;
  onReveal: () => Promise<void>;
  isLoading: boolean;
}>) {
  return (
    <GlassCard className="overflow-hidden">
      <div className="grid md:grid-cols-3">
        <div className="p-6 md:p-7">
          <div className="flex items-start gap-3">
            <Image
              src="/mail.svg"
              alt=""
              width={24}
              height={24}
              aria-hidden
              className="mt-0 icon-white"
            />
            <div>
              <h3 className="text-sm font-semibold text-white md:text-base">Email Us</h3>
              {contactInfo ? (
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="mt-0 block text-sm text-cyan-300 underline underline-offset-2 transition-colors hover:text-[#8ec5ff] md:text-base"
                >
                  {contactInfo.email}
                </a>
              ) : (
                <RevealButton onClick={onReveal} isLoading={isLoading} className="mt-0" />
              )}
            </div>
          </div>
        </div>
        <div className="border-y border-white/15 p-6 md:border-x md:border-y-0 md:p-7">
          <div className="flex items-start gap-3">
            <Image
              src="/telephone.svg"
              alt=""
              width={24}
              height={24}
              aria-hidden
              className="mt-0 icon-white"
            />
            <div>
              <h3 className="text-sm font-semibold text-white md:text-base">Call Us</h3>
              {contactInfo ? (
                <a
                  href={`tel:${contactInfo.phoneLink}`}
                  className="mt-2 block text-sm text-cyan-300 underline underline-offset-2 transition-colors hover:text-[#8ec5ff] md:text-base"
                >
                  {contactInfo.phoneDisplay}
                </a>
              ) : (
                <RevealButton onClick={onReveal} isLoading={isLoading} className="mt-2" />
              )}
            </div>
          </div>
        </div>
        <div className="p-6 md:p-7">
          <div className="flex items-start gap-3">
            <Image
              src="/clock.svg"
              alt=""
              width={24}
              height={24}
              aria-hidden
              className="mt-0 icon-white"
            />
            <div>
              <h3 className="text-sm font-semibold text-white md:text-base">Hours</h3>
              <p className="mt-2 text-xs text-white/65 md:text-sm">M - F: 9am - 5pm</p>
              <p className="mt-1 text-xs text-white/65 md:text-sm">Sat: 9am - 1pm</p>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [revealStatus, setRevealStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<string | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setCaptchaError('Please complete the captcha first.');
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      const response = await fetch('/api/contact-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: 'Contact Form Submission',
          message: form.message,
          website: '',
          captchaToken,
        }),
      });

      if (response.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', message: '' });
        resetCaptcha();
        return;
      }
      setStatus('error');
      resetCaptcha();
    } catch {
      setStatus('error');
      resetCaptcha();
    }
  };

  const renderCaptcha = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !captchaContainerRef.current || !globalThis.turnstile) {
      return;
    }

    if (captchaWidgetIdRef.current) {
      return;
    }

    captchaWidgetIdRef.current = globalThis.turnstile.render(captchaContainerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: 'dark',
      callback: (token: string) => {
        setCaptchaToken(token);
        setCaptchaError('');
      },
      'error-callback': () => {
        setCaptchaToken('');
        setCaptchaError('Captcha failed to load. Please refresh and try again.');
      },
      'expired-callback': () => {
        setCaptchaToken('');
      },
    });
  }, []);

  useEffect(() => {
    renderCaptcha();
  }, [renderCaptcha]);

  const resetCaptcha = useCallback(() => {
    if (TURNSTILE_SITE_KEY && globalThis.turnstile && captchaWidgetIdRef.current) {
      globalThis.turnstile.reset(captchaWidgetIdRef.current);
    }
    setCaptchaToken('');
  }, []);

  const handleRevealContact = async () => {
    if (contactInfo || revealStatus === 'loading') {
      return;
    }

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setCaptchaError('Please complete the captcha first.');
      return;
    }

    setRevealStatus('loading');
    try {
      const response = await fetch('/api/contact-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: '', captchaToken }),
      });

      if (!response.ok) {
        setRevealStatus('error');
        resetCaptcha();
        return;
      }

      const payload: ContactInfo = await response.json();
      setContactInfo(payload);
      setRevealStatus('idle');
      resetCaptcha();
    } catch {
      setRevealStatus('error');
      resetCaptcha();
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <Navigation />
      <RandomBackground />
      <div aria-hidden className="fixed inset-0 -z-10 bg-gradient-to-b from-black/75 via-black/25 to-black/10" />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(80% 60% at 50% 20%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)',
        }}
      />

      <main className="relative z-10 px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {TURNSTILE_SITE_KEY && (
            <Script
              src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
              strategy="lazyOnload"
              onLoad={renderCaptcha}
            />
          )}

          <header className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl">Contact Us</h1>
            <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">
              We love hearing from our customers! Whether you have a question, a bright idea, or just
              want to say hello, our team is here to help you shine. Drop us a line and we&apos;ll get
              back to you faster than you can untangle a string of Christmas lights.
            </p>
          </header>

          <section className="mt-12">
            <ContactInfoPanel
              contactInfo={contactInfo}
              onReveal={handleRevealContact}
              isLoading={revealStatus === 'loading'}
            />
          </section>

          <section className="mt-8">
            <QuickContactStrip
              contactInfo={contactInfo}
              onReveal={handleRevealContact}
              isLoading={revealStatus === 'loading'}
            />
          </section>

          {TURNSTILE_SITE_KEY && (
            <div className="mt-4 flex flex-col items-center">
              <div ref={captchaContainerRef} />
              {captchaError && (
                <p role="alert" className="mt-2 text-center text-xs text-red-300 md:text-sm">
                  {captchaError}
                </p>
              )}
            </div>
          )}

          {revealStatus === 'error' && (
            <p role="alert" className="mt-4 text-center text-xs text-red-300 md:text-sm">
              Couldn&apos;t reveal contact info right now. Please use the form below.
            </p>
          )}

          <section className="mt-14">
            <GlassCard className="mx-auto max-w-5xl p-5 sm:p-8">
              <div className="text-center">
                <h2 className="inline-flex items-center justify-center gap-3 text-2xl font-bold tracking-tight text-white md:text-3xl">
                  <Image
                    src="/send-fill.svg"
                    alt=""
                    width={38}
                    height={38}
                    aria-hidden
                    className="icon-cta-blue"
                  />
                  Send Message
                </h2>
              </div>

              <GlassCard className="mt-8 border-white/20 p-5 sm:p-7">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="mb-3 flex items-center gap-3 text-xs font-semibold tracking-wide text-white/80 md:text-sm">
                      <Image
                        src="/person.svg"
                        alt=""
                        width={30}
                        height={30}
                        aria-hidden
                        className="icon-white"
                      />
                      Your Name
                    </label>
                    <div className="relative">
                      <input
                        id="name"
                        className="w-full rounded-2xl border border-white/20 bg-black/40 px-6 py-4 text-sm text-white placeholder:text-white/40 focus:border-[#67b4ff] focus:outline-none focus:ring-1 focus:ring-[#67b4ff] md:text-base"
                        name="name"
                        type="text"
                        placeholder="John Doe"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rounded-lg bg-[#ef4444] px-2 py-1 text-sm font-bold text-white">
                        â€¢â€¢â€¢
                      </span>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="mb-3 flex items-center gap-3 text-xs font-semibold tracking-wide text-white/80 md:text-sm">
                      <Image
                        src="/envelope-fill.svg"
                        alt=""
                        width={30}
                        height={30}
                        aria-hidden
                        className="icon-white"
                      />
                      Your Email
                    </label>
                    <input
                      id="email"
                      className="w-full rounded-2xl border border-white/20 bg-black/40 px-6 py-4 text-sm text-white placeholder:text-white/40 focus:border-[#67b4ff] focus:outline-none focus:ring-1 focus:ring-[#67b4ff] md:text-base"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-3 flex items-center gap-3 text-xs font-semibold tracking-wide text-white/80 md:text-sm">
                      <Image
                        src="/text_ad.svg"
                        alt=""
                        width={30}
                        height={30}
                        aria-hidden
                        className="icon-white"
                      />
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      className="w-full rounded-2xl border border-white/20 bg-black/40 px-6 py-4 text-sm text-white placeholder:text-white/40 focus:border-[#67b4ff] focus:outline-none focus:ring-1 focus:ring-[#67b4ff] md:text-base"
                      name="message"
                      placeholder="Tell us how we can help..."
                      value={form.message}
                      onChange={handleChange}
                      rows={4}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-3 rounded-full btn-cta px-6 py-4 text-2xl font-extrabold text-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ textShadow: 'none' }}
                    disabled={status === 'sending'}
                  >
                    <Image
                      src="/send-fill.svg"
                      alt=""
                      width={30}
                      height={30}
                      aria-hidden
                      className="icon-white opacity-80"
                    />
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>

                  <p className="text-center text-xs text-white/65 md:text-sm">
                    We typically respond within 24 hours.
                  </p>

                  <div aria-live="polite" className="text-center">
                    {status === 'sent' && (
                      <p className="text-lg text-green-400">Message sent! We&apos;ll reply soon.</p>
                    )}
                    {status === 'error' && (
                      <p role="alert" className="text-lg text-red-400">
                        There was an error. Please try again.
                      </p>
                    )}
                  </div>
                </form>
              </GlassCard>
            </GlassCard>
          </section>
        </div>
      </main>
    </div>
  );
}



